/**
 * guideOptionsHelper
 *
 * Shared Copy/Share Options-dropdown builder for guide detail panels. Both
 * DiscoverySection and InsightsSection have their own independent
 * `selectedGuide` instance state, so this stays instance-free and takes a
 * getter closure rather than assuming a particular class shape — it replaces
 * 3 previously copy-pasted `console.log`-stub dropdowns with one
 * implementation.
 *
 * @module components/guideOptionsHelper
 */

import { copyToClipboard, shareOrCopy, markdownToPlainText } from '../utils/shareContent.js';

/**
 * Whether a Copy/Share source is the merged reader's `{ individual, shared }`
 * pair rather than one guide row. The merged surface is the only caller that
 * passes an object with either key, and it always passes both (one may be
 * null) — so the check is on key presence, not truthiness.
 * @param {*} source
 * @returns {boolean}
 */
function isMergedSource(source) {
  return !!source && typeof source === 'object'
    && ('individual' in source || 'shared' in source);
}

/**
 * The parts to serialize, in reading order. A single guide row is one unlabelled
 * part (every off-Library surface, byte-unchanged); the merged reader's pair is
 * one labelled part per half that is actually present, so a Together-only card
 * copies the Together part alone (D9).
 * @param {Object|null} source - A guide row, or `{ individual, shared }`
 * @returns {Array<{label: string|null, guide: Object}>}
 */
function guidePartsOf(source) {
  if (!isMergedSource(source)) return source ? [{ label: null, guide: source }] : [];
  return [
    { label: 'You', guide: source.individual },
    { label: 'Together', guide: source.shared }
  ].filter(part => !!part.guide);
}

/** The document title a Copy/Share carries, from whichever half has one. */
function guideShareTitle(source) {
  if (!isMergedSource(source)) return source?.title || '';
  return source.individual?.title || source.shared?.title || '';
}

/**
 * Serialize ONE part's sections into the shared plain-text block.
 * @param {Object} guide - Guide row (`content.sections[]`)
 * @returns {Array<string>} Lines
 */
function formatGuideSections(guide) {
  const lines = [];
  for (const section of guide.content.sections) {
    lines.push(section.heading);
    lines.push(markdownToPlainText(section.content));
    // Typed substance carriers — one per contentType (`insight` → blocks,
    // `action` → items, `thread` → threads). Each flattens back into plain-text
    // blocks so a copied guide never loses the material the panel shows; a
    // frozen pre-typed row has none of them and copies as prose alone.
    if (Array.isArray(section.blocks)) {
      for (const block of section.blocks) {
        lines.push('');
        if (block.kicker) lines.push(block.kicker);
        lines.push(markdownToPlainText(block.lead));
        lines.push(markdownToPlainText(block.body));
      }
    }
    if (Array.isArray(section.items)) {
      for (const [i, item] of section.items.entries()) {
        lines.push('');
        if (item.kicker) lines.push(item.kicker);
        lines.push(`${i + 1}. ${item.title}`);
        lines.push(markdownToPlainText(item.body));
      }
    }
    if (Array.isArray(section.threads)) {
      for (const thread of section.threads) {
        lines.push('');
        if (thread.headline) lines.push(thread.headline);
        lines.push(markdownToPlainText(thread.question));
        if (thread.context) lines.push(markdownToPlainText(thread.context));
      }
    }
    lines.push('');
  }
  return lines;
}

/**
 * Format a guide's content as shareable plain text.
 *
 * Takes either one guide row (every off-Library surface — output unchanged) or
 * the merged reader's `{ individual, shared }` pair, in which case each half is
 * serialized under its part label, "You" then "Together" (D9). The receipt and
 * the sharing switch are never copied: they are controls and own-data
 * bookkeeping, not the document.
 *
 * @param {Object|null} source - A guide row, or `{ individual, shared }`
 * @returns {string} Plain text, or '' when no part has loaded content yet
 */
export function formatGuideForSharing(source) {
  const parts = guidePartsOf(source)
    .filter(({ guide }) => guide?.content && Array.isArray(guide.content.sections));
  if (parts.length === 0) return '';

  const title = guideShareTitle(source);
  const subtitle = parts[0].guide.subtitle;
  const lines = [];
  if (title) lines.push(title);
  if (subtitle) lines.push(subtitle);
  lines.push('');

  for (const { label, guide } of parts) {
    if (label) {
      lines.push(label);
      lines.push('');
    }
    lines.push(...formatGuideSections(guide));
  }

  return lines.join('\n').trim();
}

/**
 * Build the `optionsDropdown` payload (Copy/Share) for a guide detail panel.
 * @param {() => Object|null} getGuide - Reads the currently-loaded guide at click
 *   time (the panel opens before `API.getGuide()` resolves, so this must be a
 *   getter, not a value). The merged `/library` reader passes
 *   `{ individual, shared }` instead of one row — see {@link formatGuideForSharing}.
 * @returns {{ items: Array, onAction: Function }}
 */
export function buildGuideOptionsDropdown(getGuide) {
  return {
    items: [
      { action: 'copy', label: 'Copy' },
      { action: 'share', label: 'Share' }
    ],
    onAction: async (action) => {
      const guide = getGuide();
      // Guard the same async-load race handleDiscussGuide already guards
      // against (the button is live before API.getGuide resolves).
      if (!guide) return;
      try {
        const text = formatGuideForSharing(guide);
        if (!text) return; // content not loaded yet — mirrors renderGuideDetailPanel's own guard
        if (action === 'copy') await copyToClipboard(text, { successMessage: 'Guide copied' });
        else if (action === 'share') await shareOrCopy({ title: guideShareTitle(guide), text });
      } catch (error) {
        console.error('[guideOptionsHelper] Copy/share failed:', error);
        window.notificationManager?.showError('Something went wrong. Please try again.');
      }
    }
  };
}

/**
 * The full Options dropdown for a guide-management surface: Copy/Share (from
 * {@link buildGuideOptionsDropdown}) plus Archive/Unarchive.
 *
 * Every surface that can act on a guide builds its menu here — /library's
 * detail panel and row kebab, /search's panel, and /discovery's two path-side
 * guide panels — so the item set, ordering, and Archive-label logic can't
 * drift between them. Copy/Share stay internal; only Archive reaches the
 * caller's `onOption`.
 *
 * There is no Delete row on any guide surface: a guide is written from a
 * completed path and is immutable once READY, so Archive (reversible, and on
 * /library it acts on every present half of the card) is the whole
 * put-it-away vocabulary. `POST /api/guides/delete` survives as a support
 * route with no UI caller.
 *
 * @param {() => Object|null} getGuide - Reads the guide whose CONTENT is being
 *   copied/shared, at click time (panels open before their fetch resolves)
 * @param {Object} opts
 * @param {boolean} opts.archived - Drives the Archive ↔ Unarchive label. Callers
 *   source this from whatever they hold: a summary row's `.archived` boolean, a
 *   raw `UserGuide` row's `.archivedAt` timestamp, or (on the merged /library
 *   card) the all-present-halves rule.
 * @param {(action: 'archive') => void} opts.onOption - Handler for the one
 *   management action
 * @returns {{ items: Array, onAction: Function }}
 */
export function buildGuideManagementDropdown(getGuide, { archived, onOption }) {
  const sharing = buildGuideOptionsDropdown(getGuide);
  return {
    items: [
      ...sharing.items,
      { action: 'archive', label: archived ? 'Unarchive' : 'Archive' }
    ],
    onAction: (action) => (action === 'copy' || action === 'share'
      ? sharing.onAction(action)
      : onOption(action))
  };
}
